import {BeneAziendaleDossierModel, DirittoAziendaleModel, TipoDiritto, TipologiaImmobile} from '../../../../../../shared/models';

export interface NodoGrafo {
	id: string;
	name: string;
	type: 'person' | 'company' | 'asset';
	isRoot?: boolean;
	x?: number;
	y?: number;
}

export interface QuotaArco {
	quota: number;
	/** sigla del diritto già tradotta: PP, NP, U */
	sigla: string;
}

export interface ArcoGrafo {
	/** id in ingresso, riferimento al nodo dopo calcolaLayoutAlbero */
	source: any;
	target: any;
	/** somma delle quote: serve solo a scegliere il proprietario principale nell'albero */
	quota: number;
	/** una voce per diritto: lo stesso soggetto può averne più d'uno sulla stessa azienda */
	quote: QuotaArco[];
	/** etichetta già composta: la usa la freccia verso gli immobili, che non ha quote */
	righe?: string[];
}

export interface OpzioniLayout {
	/** larghezza del contenitore: è il minimo garantito, l'albero può chiedere di più */
	larghezzaDisponibile: number;
	/** passo orizzontale tra due colonne dell'albero */
	spaziaturaX?: number;
	/** distanza verticale tra due livelli */
	altezzaRiga?: number;
	margineTop?: number;
	margineBottom?: number;
	altezzaMinima?: number;
}

export interface RisultatoLayout {
	width: number;
	height: number;
}

const SPAZIATURA_X_DEFAULT = 200;
/** interlinea delle etichette a più righe, in scala con il font da 11px */
const ALTEZZA_INTERLINEA = 13;
const ALTEZZA_RIGA_DEFAULT = 140;
const MARGINE_X = 80;

/**
 * Dispone i nodi ad albero: la radice in alto, e ogni nodo posseduto sotto il suo proprietario.
 * Le foglie occupano colonne consecutive e ogni genitore si centra sui propri figli, così i
 * sottoalberi non si sovrappongono e le partecipazioni di un'azienda restano sotto di essa.
 *
 * Restituisce la dimensione naturale dell'albero, che cresce quanto serve: chi ha spazio la usa come
 * tela e scrolla, chi ha una tela fissa (il PDF) ci mappa sopra il viewBox e lascia scalare l'SVG.
 */
export function calcolaLayoutAlbero(
	nodi: NodoGrafo[],
	archi: ArcoGrafo[],
	rootId: string,
	opzioni: OpzioniLayout
): RisultatoLayout {
	const mappaNodi = new Map(nodi.map(nodo => [nodo.id, nodo]));

	// gli archi arrivano con source/target testuali: li risolvo una volta sola, come faceva forceLink
	archi.forEach(arco => {
		if (typeof arco.source === 'string') {
			arco.source = mappaNodi.get(arco.source) ?? arco.source;
		}
		if (typeof arco.target === 'string') {
			arco.target = mappaNodi.get(arco.target) ?? arco.target;
		}
	});

	// Un'azienda può avere più proprietari, ma nell'albero sta sotto uno solo: si sceglie quello con
	// la quota maggiore. Gli altri possessi restano disegnati come archi in più.
	const genitore = new Map<string, string>();
	const quotaDelGenitore = new Map<string, number>();
	archi.forEach(arco => {
		const idFiglio = arco.target?.id;
		const idGenitore = arco.source?.id;
		if (!idFiglio || !idGenitore || idFiglio === idGenitore) {
			return;
		}
		const quota = arco.quota || 0;
		if (!genitore.has(idFiglio) || quota > (quotaDelGenitore.get(idFiglio) ?? -1)) {
			genitore.set(idFiglio, idGenitore);
			quotaDelGenitore.set(idFiglio, quota);
		}
	});

	// partecipazioni incrociate (A possiede B che possiede A): senza questo controllo l'albero si chiude su sé stesso
	const risaleFinoA = (partenza: string, cercato: string) => {
		const visti = new Set<string>([partenza]);
		let corrente = genitore.get(partenza);
		while (corrente && !visti.has(corrente)) {
			if (corrente === cercato) {
				return true;
			}
			visti.add(corrente);
			corrente = genitore.get(corrente);
		}
		return false;
	};

	const figli = new Map<string, NodoGrafo[]>();
	nodi.forEach(nodo => figli.set(nodo.id, []));

	const senzaGenitore: NodoGrafo[] = [];
	nodi.forEach(nodo => {
		if (nodo.id === rootId) {
			return;
		}
		const idGenitore = genitore.get(nodo.id);
		if (idGenitore && mappaNodi.has(idGenitore) && !risaleFinoA(idGenitore, nodo.id)) {
			figli.get(idGenitore)!.push(nodo);
		} else {
			senzaGenitore.push(nodo);
		}
	});

	// chi non ha un proprietario riconosciuto pende comunque dalla radice, altrimenti sparirebbe dal grafo
	if (mappaNodi.has(rootId)) {
		senzaGenitore.forEach(nodo => figli.get(rootId)!.push(nodo));
	}

	const colonna = new Map<string, number>();
	const profondita = new Map<string, number>();
	const visitati = new Set<string>();
	let prossimaColonna = 0;

	const visita = (nodo: NodoGrafo, livello: number) => {
		if (visitati.has(nodo.id)) {
			return;
		}
		visitati.add(nodo.id);
		profondita.set(nodo.id, livello);

		const suoiFigli = (figli.get(nodo.id) ?? []).filter(figlio => !visitati.has(figlio.id));
		if (suoiFigli.length === 0) {
			colonna.set(nodo.id, prossimaColonna++);
			return;
		}

		suoiFigli.forEach(figlio => visita(figlio, livello + 1));
		const colonneFigli = suoiFigli.map(figlio => colonna.get(figlio.id)).filter((col): col is number => col !== undefined);
		colonna.set(nodo.id, colonneFigli.length ? (Math.min(...colonneFigli) + Math.max(...colonneFigli)) / 2 : prossimaColonna++);
	};

	const nodoRadice = mappaNodi.get(rootId);
	if (nodoRadice) {
		visita(nodoRadice, 0);
	}
	// rete di sicurezza: un nodo non raggiunto finisce in coda invece di accatastarsi sulla colonna 0
	nodi.forEach(nodo => {
		if (!visitati.has(nodo.id)) {
			visita(nodo, 1);
		}
	});

	const numeroColonne = Math.max(1, prossimaColonna);
	const profonditaMassima = Math.max(0, ...Array.from(profondita.values()));

	const spaziaturaRichiesta = opzioni.spaziaturaX ?? SPAZIATURA_X_DEFAULT;
	const altezzaRigaRichiesta = opzioni.altezzaRiga ?? ALTEZZA_RIGA_DEFAULT;
	const margineTop = opzioni.margineTop ?? 70;
	const margineBottom = opzioni.margineBottom ?? 80;

	const width = Math.max(opzioni.larghezzaDisponibile, MARGINE_X * 2 + (numeroColonne - 1) * spaziaturaRichiesta);
	const height = Math.max(
		opzioni.altezzaMinima ?? 400,
		margineTop + profonditaMassima * altezzaRigaRichiesta + margineBottom
	);

	// con poche colonne su una tela larga l'albero resta centrato invece di accostarsi a sinistra
	const larghezzaContenuto = (numeroColonne - 1) * spaziaturaRichiesta;
	const offsetX = (width - larghezzaContenuto) / 2;

	nodi.forEach(nodo => {
		nodo.x = offsetX + (colonna.get(nodo.id) ?? 0) * spaziaturaRichiesta;
		nodo.y = margineTop + (profondita.get(nodo.id) ?? 0) * altezzaRigaRichiesta;
	});

	return {width, height};
}

/**
 * Costruisce nodi e archi del grafo a partire dalle aziende del dossier. La radice è il soggetto,
 * ogni azienda pende dai suoi proprietari e gli immobili stanno tutti in un unico nodo sotto la
 * propria azienda: un nodo per immobile riempirebbe il grafo di colonne per chi ne ha molti.
 */
export function costruisciDatiGrafo(
	aziende: BeneAziendaleDossierModel[],
	rootId: string,
	rootName: string,
	traduci: (chiave: string, parametri?: any) => string
): {nodes: NodoGrafo[]; links: ArcoGrafo[]} {
	const nodes: NodoGrafo[] = [];
	const links: ArcoGrafo[] = [];
	const mappaNodi = new Map<string, NodoGrafo>();

	const nodoRadice: NodoGrafo = {id: rootId, name: rootName, type: 'person', isRoot: true};
	nodes.push(nodoRadice);
	mappaNodi.set(rootId, nodoRadice);

	const registraAzienda = (azienda: BeneAziendaleDossierModel) => {
		if (!azienda?.id || mappaNodi.has(azienda.id)) {
			return;
		}
		const nodo: NodoGrafo = {id: azienda.id, name: azienda.descrizione, type: 'company'};
		nodes.push(nodo);
		mappaNodi.set(azienda.id, nodo);
	};

	aziende?.forEach(registraAzienda);

	// una sola freccia per coppia proprietario -> posseduto, anche quando i diritti sono più d'uno
	const archiPerCoppia = new Map<string, ArcoGrafo>();

	aziende?.forEach(azienda => {
		azienda.diritti?.forEach((diritto: DirittoAziendaleModel) => {
			const ownerId = diritto.soggettoId;
			if (!ownerId) {
				return;
			}
			// il proprietario può essere un'azienda comparsa più avanti nella lista: qui è già registrata
			if (!mappaNodi.has(ownerId)) {
				return;
			}

			const chiave = `${ownerId}->${azienda.id}`;
			let arco = archiPerCoppia.get(chiave);
			if (!arco) {
				arco = {source: ownerId, target: azienda.id, quota: 0, quote: []};
				archiPerCoppia.set(chiave, arco);
				links.push(arco);
			}

			const quota = diritto.quotaDirittoPercent || 0;
			arco.quota += quota;
			arco.quote.push({quota, sigla: siglaDiritto(diritto.diritto, traduci)});
		});

		// un solo nodo immobili per azienda, con quanti fabbricati e quanti terreni sulla freccia
		const righeImmobili = righeConteggioImmobili((azienda as any).immobili, traduci);
		if (righeImmobili.length) {
			const idImmobili = `imm-${azienda.id}`;
			if (!mappaNodi.has(idImmobili)) {
				const nodo: NodoGrafo = {id: idImmobili, name: traduci('grafoAziende.nodoImmobili'), type: 'asset'};
				nodes.push(nodo);
				mappaNodi.set(idImmobili, nodo);
			}

			links.push({source: azienda.id, target: idImmobili, quota: 0, quote: [], righe: righeImmobili});
		}
	});

	return {nodes, links};
}

/**
 * Etichetta della freccia verso gli immobili: quanti fabbricati e quanti terreni. Le righe a zero
 * non compaiono, e se l'azienda non ha immobili non torna nulla e il nodo non viene creato.
 */
function righeConteggioImmobili(immobili: any[], traduci: (chiave: string, parametri?: any) => string): string[] {
	let fabbricati = 0;
	let terreni = 0;
	immobili?.forEach(immobile => {
		if (!immobile?.tipoImmobile) {
			return;
		}
		isTerreno(immobile.tipoImmobile) ? terreni++ : fabbricati++;
	});

	const righe: string[] = [];
	if (fabbricati) {
		righe.push(traduci(fabbricati === 1 ? 'grafoAziende.nrFabbricato' : 'grafoAziende.nrFabbricati', {numero: fabbricati}));
	}
	if (terreni) {
		righe.push(traduci(terreni === 1 ? 'grafoAziende.nrTerreno' : 'grafoAziende.nrTerreni', {numero: terreni}));
	}
	return righe;
}

/** stessa regola di PatrimonioImmobiliareService.isTerreno, qui in locale per non legare il layout ai service */
function isTerreno(tipoImmobile: string): boolean {
	return tipoImmobile === TipologiaImmobile.TERRENO_AGRICOLO || tipoImmobile === TipologiaImmobile.TERRENO_EDIFICABILE;
}

const CHIAVE_SIGLA: Record<string, string> = {
	[TipoDiritto.PIENA_PROPRIETA]: 'siglaPienaProprieta',
	[TipoDiritto.NUDA_PROPRIETA]: 'siglaNudaProprieta',
	[TipoDiritto.USUFRUTTO]: 'siglaUsufrutto'
};

/** sigla tradotta del diritto, vuota se il diritto non è fra quelli noti */
function siglaDiritto(diritto: string, traduci: (chiave: string) => string): string {
	const chiave = CHIAVE_SIGLA[diritto];
	return chiave ? traduci(chiave) : '';
}

/**
 * Etichetta della freccia, una riga per diritto: "2.00% PP", "5.00% NP". Le quote di uno stesso
 * proprietario stanno tutte sulla stessa freccia, incolonnate invece che stampate una sopra l'altra.
 */
export function righeEtichettaArco(arco: ArcoGrafo): string[] {
	if (arco.righe) {
		return arco.righe;
	}
	return (arco.quote ?? [])
		.filter(voce => voce.quota)
		.map(voce => (voce.sigla ? `${voce.quota.toFixed(2)}% ${voce.sigla}` : `${voce.quota.toFixed(2)}%`));
}

/**
 * Scrive le righe dentro un <text> SVG come tspan incolonnati e centrati sul punto di ancoraggio,
 * che per le etichette delle frecce è la metà del segmento.
 */
export function scriviEtichettaMultiriga(testo: SVGTextElement, righe: string[], interlinea = ALTEZZA_INTERLINEA): void {
	while (testo.firstChild) {
		testo.removeChild(testo.firstChild);
	}
	// il blocco di righe va centrato verticalmente: la prima parte sopra l'ancoraggio di metà altezza
	const partenza = -((righe.length - 1) / 2) * interlinea + 4;
	righe.forEach((riga, indice) => {
		const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
		tspan.setAttribute('x', '0');
		tspan.setAttribute('dy', String(indice === 0 ? partenza : interlinea));
		tspan.textContent = riga;
		testo.appendChild(tspan);
	});
}

/** allarga il rettangolo di sfondo di un gruppo etichetta finché copre il testo che contiene */
export function adattaSfondoEtichetta(gruppo: SVGGElement): void {
	const testo = gruppo.querySelector('text') as SVGTextElement | null;
	const sfondo = gruppo.querySelector('rect');
	if (!testo || !sfondo || typeof testo.getBBox !== 'function') {
		return;
	}
	try {
		const bbox = testo.getBBox();
		sfondo.setAttribute('x', String(bbox.x - 4));
		sfondo.setAttribute('y', String(bbox.y - 2));
		sfondo.setAttribute('width', String(bbox.width + 8));
		sfondo.setAttribute('height', String(bbox.height + 4));
	} catch (err) {
		// getBBox fallisce se il nodo non è ancora nel layout: l'etichetta resta senza sfondo
	}
}
